"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAssistantHistory = void 0;
const react_1 = require("react");
const assistant_conversations_1 = __importDefault(require("@app/request/assistant-conversations"));
const util_1 = require("@app/util");
// The server caps a page at 100. One page is all the panel offers: this is a read-only archive
// for glancing back at what was said, not a corpus to page through, and a manager who needs the
// 51st most recent conversation is better served by a search we have not built than by a
// "load more" button they would click five times.
const HISTORY_PAGE_SIZE = 50;
const initialState = { isLoading: false, conversations: [], hasLoaded: false };
const reducer = (state, action) => {
    switch (action.type) {
        case 'RESET':
            return initialState;
        case 'LOADING_START':
            return { ...state, isLoading: true };
        case 'LOADING_END':
            return { isLoading: false, conversations: action.payload, hasLoaded: true };
        default:
            return state;
    }
};
/**
 * The manager's own archived conversations on the current workspace.
 *
 * Fetched when the panel is first opened rather than on mount, so a manager who never looks at
 * their history never pays for it.
 *
 * 🔴 **A workspace change RESETS this, and the reset is not an optimisation.** A manager can
 * hold several workspaces, and showing the previous workspace's conversations after a switch
 * would leak one workspace's content into another's screen. The state is dropped the moment
 * `workspaceId` changes — before any new request resolves — so there is no frame in which the
 * old list is visible under the new workspace. The refetch is also guarded against a late
 * answer for the workspace just left.
 */
const useAssistantHistory = (workspaceId, isOpen) => {
    const [state, dispatch] = (0, react_1.useReducer)(reducer, initialState);
    // Its own effect, and deliberately not merged with the fetch below: this must clear on a
    // workspace change whether or not the panel happens to be open at the time, so that opening
    // it later cannot show a stale list for a moment before the new answer lands.
    (0, react_1.useEffect)(() => {
        dispatch({ type: 'RESET' });
    }, [workspaceId]);
    (0, react_1.useEffect)(() => {
        if (!isOpen || !(0, util_1.isValue)(workspaceId))
            return;
        let isCurrent = true;
        dispatch({ type: 'LOADING_START' });
        void (0, assistant_conversations_1.default)({
            workspace: workspaceId,
            limit: HISTORY_PAGE_SIZE,
        })
            // A transport failure rejects rather than resolving with `errors`, so it bypasses
            // `processErrors` — see the same catch in `use-assistant-usage.ts`. An empty archive is
            // the honest thing to show, and leaving it uncaught would be an unhandled rejection.
            .catch(() => undefined)
            .then((result) => {
            // The answer for a workspace we have already left must not land in state.
            if (isCurrent)
                dispatch({ type: 'LOADING_END', payload: result ?? [] });
        });
        return () => {
            isCurrent = false;
        };
    }, [workspaceId, isOpen]);
    return state;
};
exports.useAssistantHistory = useAssistantHistory;
exports.default = exports.useAssistantHistory;
